export const emptyWorkspace = () => ({version: 1, links: [], models: [], drawingScales: {}});
const fail = message => { throw Object.assign(new Error(message), {status: 400}); };
const finite = n => typeof n === 'number' && Number.isFinite(n) && Math.abs(n) <= 1e12;
const fileId = id => typeof id === 'string' && /^[a-f0-9]{1,80}$/.test(id);
export function validateWorkspace(input) {
  if (!input || typeof input !== 'object' || Array.isArray(input)) fail('Viewer 工作階段格式錯誤');
  const {links = [], models = [], drawingScales = {}} = input;
  if (!Array.isArray(links) || links.length > 100 || !Array.isArray(models) || models.length > 20) fail('圖面關聯或模型數量超過上限');
  const engines = e => ['oda','libredwg'].includes(e);
  const record = value => value && typeof value === 'object' && !Array.isArray(value);
  const result = emptyWorkspace();
  result.links = links.map(l => {
    if (!record(l) || !fileId(l.drawingId) || !engines(l.engine) || typeof l.floorName !== 'string' || l.floorName.length > 100 || !finite(l.z)) fail('圖面關聯無效');
    if (!Array.isArray(l.controls) || l.controls.length > 20) fail('控制點超過上限');
    const controls = l.controls.map(c => {
      if (!record(c) || ![c.drawing,c.model].every(p => Array.isArray(p) && p.length === 2 && p.every(finite))) fail('控制點座標無效');
      return {drawing:[...c.drawing],model:[...c.model]};
    });
    if (typeof l.flipY !== 'boolean') fail('圖面軸向無效');
    if (controls.length >= 2) fitMapping(controls,l.flipY);
    return {drawingId:l.drawingId,engine:l.engine,floorName:l.floorName.trim(),z:l.z,flipY:l.flipY,controls};
  });
  const keys=new Set();
  result.models = models.map(m => {
    if (!record(m) || !fileId(m.id) || !engines(m.engine) || typeof m.visible !== 'boolean' || !finite(m.scale) || m.scale <= 0 || !finite(m.rotation) || !Array.isArray(m.offset) || m.offset.length !== 3 || !m.offset.every(finite)) fail('模型變換無效');
    const key=`${m.id}:${m.engine}`;if(keys.has(key))fail('重複模型');keys.add(key);
    return {id:m.id,engine:m.engine,visible:m.visible,scale:m.scale,rotation:m.rotation,offset:[...m.offset]};
  });
  if (!drawingScales || typeof drawingScales !== 'object' || Array.isArray(drawingScales) || Object.keys(drawingScales).length > 100) fail('圖面尺度格式錯誤');
  for(const [key,value] of Object.entries(drawingScales)) {
    if(!/^[a-f0-9]{1,80}:(oda|libredwg)$/.test(key)||!finite(value)||value<=0)fail('圖面尺度無效');
    result.drawingScales[key]=value;
  }
  return result;
}
// Least squares similarity transform, optionally reflecting SVG's downward Y.
export function fitMapping(controls, flipY = true) {
  if (controls.length < 2) fail('至少需要兩組控制點');
  const points=controls.map(c=>({u:c.drawing[0],v:c.drawing[1]*(flipY?-1:1),x:c.model[0],y:c.model[1]}));
  const avg=k=>points.reduce((s,p)=>s+p[k],0)/points.length;
  const u=avg('u'),v=avg('v'),x=avg('x'),y=avg('y');let den=0,a=0,b=0;
  for(const p of points){const du=p.u-u,dv=p.v-v,dx=p.x-x,dy=p.y-y;den+=du*du+dv*dv;a+=du*dx+dv*dy;b+=du*dy-dv*dx;}
  if(den<=1e-20)fail('圖面控制點不可重合');a/=den;b/=den;
  if(!Number.isFinite(a)||!Number.isFinite(b)||Math.hypot(a,b)<=1e-12)fail('模型控制點不可重合或尺度無效');
  const mapping={a,b,tx:x-a*u+b*v,ty:y-b*u-a*v,flipY};
  const residuals=controls.map(c=>{const p=toModel(mapping,c.drawing);return Math.hypot(p[0]-c.model[0],p[1]-c.model[1]);});
  return {...mapping,residuals,rms:Math.sqrt(residuals.reduce((s,n)=>s+n*n,0)/residuals.length)};
}
export function toModel(m,[u,v]) {v*=m.flipY?-1:1;return [m.a*u-m.b*v+m.tx,m.b*u+m.a*v+m.ty];}
export function toDrawing(m,[x,y]) {x-=m.tx;y-=m.ty;const d=m.a*m.a+m.b*m.b;return [(m.a*x+m.b*y)/d,(-m.b*x+m.a*y)/d*(m.flipY?-1:1)];}
